import { useState } from 'react';
import '../tailwind.css';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/Reporting/ui/card';
import ReportForm from '../../../components/Reporting/report-form';
import TopSellerItemsTable from '../../../components/Reporting/top-seller-items-table';
import { REPORT_PROVIDERS } from '../../../components/Reporting/shared/schema';
import { HeaderMenu } from '../../../components/Reporting/header-menu';
import instance from '../../../services/api';
import { toast } from 'react-toastify';
import { TrendingUp } from 'lucide-react';

export default function TopSellerItemsPage() {
  const [allItems, setAllItems] = useState([]); // Accumulated items
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [filterParams, setFilterParams] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const pageSize = 20; // Fixed page size for Load More pattern

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    setCurrentPage(0); // Reset to first page on new filter
    setFilterParams(data);
    setAllItems([]); // Clear accumulated items

    try {
      // Build request body
      const requestBody = {
        businessUuid: data.businessUuid,
        startDate: data.startDate,
        endDate: data.endDate,
        startTime: data.startTime,
        endTime: data.endTime,
        filter: data.filter || 'all',
        useLocalTime: data.useLocalTime || false,
        includeModifierOption: data.includeModifierOption || false,
        providers: data.providers || REPORT_PROVIDERS,
        pageSize: pageSize,
        page: 0,
      };

      const response = await instance.post(
        '/reports/top-selling-items',
        requestBody
      );

      const newItems = response.data.items || [];
      const pagination = response.data.pagination || {};

      setAllItems(newItems);
      setHasMoreData(pagination.hasNextPage || false);
      setTotalItems(pagination.totalItems || newItems.length);
      setCurrentPage(0);

      toast.success('Top Seller Items Report Generated Successfully');

      // Scroll to report output
      setTimeout(() => {
        document.getElementById('top-seller-items-report')?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 100);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to generate top seller items report. Please try again.';
      toast.error(errorMessage);
      setAllItems([]);
      setHasMoreData(false);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!filterParams || isLoadingMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const requestBody = {
        businessUuid: filterParams.businessUuid,
        startDate: filterParams.startDate,
        endDate: filterParams.endDate,
        startTime: filterParams.startTime,
        endTime: filterParams.endTime,
        filter: filterParams.filter || 'all',
        useLocalTime: filterParams.useLocalTime || false,
        includeModifierOption: filterParams.includeModifierOption || false,
        providers: filterParams.providers || REPORT_PROVIDERS,
        pageSize: pageSize,
        page: nextPage,
      };

      const response = await instance.post(
        '/reports/top-selling-items',
        requestBody
      );

      const newItems = response.data.items || [];
      const pagination = response.data.pagination || {};

      // Append new items to existing items
      setAllItems((prevItems) => [...prevItems, ...newItems]);
      setHasMoreData(pagination.hasNextPage || false);
      setCurrentPage(nextPage);

      toast.success(`Loaded ${newItems.length} more items`);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to load more items. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderMenu
        currentPage="top-sellers"
        reportData={allItems.length > 0 ? { items: allItems } : null}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Form */}
        <Card className="no-print mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="text-primary mr-3 h-6 w-6" />
              Generate Top Seller Items Report
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportForm
              onSubmit={handleFormSubmit}
              isLoading={isLoading}
              setReportData={(data) => setAllItems(data || [])}
              showIncludeModifierOption={true}
              showSections={false}
            />
          </CardContent>
        </Card>

        {/* Loading State */}
        {isLoading && allItems.length === 0 && (
          <Card className="mb-8">
            <CardContent className="pt-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                <span className="text-foreground">Generating report...</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Seller Items Report Output */}
        {allItems.length > 0 && (
          <div id="top-seller-items-report">
            <TopSellerItemsTable
              items={allItems}
              isLoading={isLoading}
              isLoadingMore={isLoadingMore}
              hasMoreData={hasMoreData}
              onLoadMore={handleLoadMore}
              totalItems={totalItems}
            />
          </div>
        )}
      </div>
    </div>
  );
}
